import React, { useState, useRef, createElement } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "../context/TranslationContext";
import { DEFAULT_MODELS, ModelConfig } from "../data/translationService";
import { ArrowLeft, Key, Eye, EyeOff, Cpu, RotateCcw, Check, Globe2, ExternalLink, Download, Upload, Archive } from "lucide-react";
import { toast } from "sonner";
import { ApiEnvironment, API_ENV_PRESETS, getApiEndpoints, getApiEnvironment, setApiEnvironment } from "../data/config";
const DRAFT_COLORS: Record<string, {
  bg: string;
  border: string;
  text: string;
}> = {
  A: {
    bg: "bg-blue-50",
    border: "border-blue-200",
    text: "text-blue-700"
  },
  B: {
    bg: "bg-violet-50",
    border: "border-violet-200",
    text: "text-violet-700"
  },
  C: {
    bg: "bg-teal-50",
    border: "border-teal-200",
    text: "text-teal-700"
  }
};
export const Settings: React.FC = () => {
  const {
    openRouterApiKey,
    setOpenRouterApiKey,
    modelConfigs,
    setModelConfigs,
    glossary,
    setGlossaryFromBackup,
    segmentOverrides,
    setSegmentOverridesFromBackup,
    restoreDocumentStatesFromBackup,
    documentStates
  } = useTranslation();
  const [apiKey, setApiKey] = useState(openRouterApiKey);
  const [apiEnvironment, setApiEnvironmentState] = useState<ApiEnvironment>(getApiEnvironment());
  const [customLawvoApi, setCustomLawvoApi] = useState(getApiEndpoints().lawvoApi);
  const [customDcApi, setCustomDcApi] = useState(getApiEndpoints().dcApi);
  const [showKey, setShowKey] = useState(false);
  const [models, setModels] = useState<ModelConfig[]>(modelConfigs);
  const [hasChanges, setHasChanges] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingBackup, setPendingBackup] = useState<any>(null);
  const [restoreTranslations, setRestoreTranslations] = useState(true);
  const handleApiKeyChange = (value: string) => {
    setApiKey(value);
    setHasChanges(true);
  };
  const handleModelChange = (draftId: "A" | "B" | "C", field: "id" | "label", value: string) => {
    setModels(prev => prev.map(m => m.draftId === draftId ? {
      ...m,
      [field]: value
    } : m));
    setHasChanges(true);
  };
  const handleSave = () => {
    setOpenRouterApiKey(apiKey);
    setModelConfigs(models);
    setApiEnvironment(apiEnvironment, {
      lawvoApi: customLawvoApi,
      dcApi: customDcApi
    });
    setHasChanges(false);
    toast.success("Settings saved — reload the page if API endpoints changed");
  };
  const handleResetModels = () => {
    setModels(DEFAULT_MODELS);
    setHasChanges(true);
  };
  const handleExport = () => {
    const legacyOverrides: Record<string, {
      status: string;
      mergedText?: string;
      sourceText?: string;
    }> = {};
    for (const [key, state] of Object.entries(segmentOverrides)) {
      legacyOverrides[key] = {
        status: state.status,
        mergedText: state.mergedText,
        sourceText: state.sourceText
      };
    }
    const backup = {
      version: "2.0",
      exportedAt: new Date().toISOString(),
      settings: {
        apiKey: openRouterApiKey,
        models: modelConfigs,
        apiEnvironment,
        customEndpoints: {
          lawvoApi: customLawvoApi,
          dcApi: customDcApi
        }
      },
      glossary,
      segmentStates: documentStates,
      segmentOverrides: legacyOverrides
    };
    const blob = new Blob([JSON.stringify(backup, null, 2)], {
      type: "application/json"
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lawvo-backup-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Backup downloaded");
  };
  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = event => {
      try {
        const backup = JSON.parse(event.target?.result as string);
        if (!backup.version || !backup.settings || !backup.glossary) {
          toast.error("Invalid backup file format");
          return;
        }
        if (backup.version !== "1.0" && backup.version !== "2.0") {
          toast.error(`Unsupported backup version: ${backup.version}`);
          return;
        }
        setPendingBackup(backup);
        setRestoreTranslations(true);
      } catch {
        toast.error("Failed to parse backup file");
      }
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  const confirmRestore = () => {
    if (!pendingBackup) return;
    const backup = pendingBackup;
    // Restore API key
    if (backup.settings.apiKey) {
      setOpenRouterApiKey(backup.settings.apiKey);
      setApiKey(backup.settings.apiKey);
    }
    // Restore models
    if (Array.isArray(backup.settings.models) && backup.settings.models.length === 3) {
      setModelConfigs(backup.settings.models);
      setModels(backup.settings.models);
    }
    if (backup.settings.apiEnvironment) {
      setApiEnvironmentState(backup.settings.apiEnvironment);
      setHasChanges(true);
    }
    if (backup.settings.customEndpoints) {
      setCustomLawvoApi(backup.settings.customEndpoints.lawvoApi || customLawvoApi);
      setCustomDcApi(backup.settings.customEndpoints.dcApi || customDcApi);
    }
    // Restore glossary
    if (Array.isArray(backup.glossary)) {
      setGlossaryFromBackup(backup.glossary);
    }
    // Restore segment state — v2 preferred, v1 fallback
    let overrideCount = 0;
    if (restoreTranslations && backup.version === "2.0" && backup.segmentStates && typeof backup.segmentStates === "object") {
      overrideCount = Object.values(backup.segmentStates).reduce((n: number, states) => n + (Array.isArray(states) ? states.length : 0), 0);
      restoreDocumentStatesFromBackup(backup.segmentStates);
    } else if (restoreTranslations && backup.segmentOverrides && typeof backup.segmentOverrides === "object") {
      overrideCount = Object.keys(backup.segmentOverrides).length;
      setSegmentOverridesFromBackup(backup.segmentOverrides);
    }
    const parts = [`${backup.glossary.length} glossary entries`];
    if (restoreTranslations && overrideCount > 0) {
      parts.push(`${overrideCount} segment overrides`);
    }
    toast.success(`Backup restored (${parts.join(", ")}${backup.exportedAt ? ", from " + new Date(backup.exportedAt).toLocaleDateString() : ""})`);
    setPendingBackup(null);
  };
  return <div className="min-h-screen bg-slate-50 p-8" data-id="element-662">
      <div className="max-w-3xl mx-auto space-y-8" data-id="element-663">
        {/* Header */}
        <div className="flex items-center justify-between" data-id="element-664">
          <div data-id="element-665">
            <Link to="/translation" className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors mb-3" data-id="element-666">
              <ArrowLeft className="w-4 h-4" data-id="element-667" />
              Back to Documents
            </Link>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3" data-id="element-668">
              <div className="p-2 bg-slate-900 text-white rounded-lg" data-id="element-669">
                <Globe2 className="w-6 h-6" data-id="element-670" />
              </div>
              Settings
            </h1>
          </div>
          <button onClick={handleSave} disabled={!hasChanges} className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm" data-id="element-671">
            <Check className="w-4 h-4" data-id="element-672" />
            Save Changes
          </button>
        </div>

        {/* API Environment */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-673">
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50" data-id="element-674">
            <h2 className="font-semibold text-slate-900" data-id="element-675">API Environment</h2>
            <p className="text-sm text-slate-500 mt-1" data-id="element-676">
              Connect to production, staging, or local backends
            </p>
          </div>
          <div className="p-6 space-y-4" data-id="element-677">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-id="element-678">
              {([["production", "Production"], ["staging", "Staging"], ["local", "Local"], ["custom", "Custom"]] as const).map(([value, label]) => <button key={value} type="button" onClick={() => {
              setApiEnvironmentState(value);
              if (value !== "custom") {
                const preset = API_ENV_PRESETS[value];
                setCustomLawvoApi(preset.lawvoApi);
                setCustomDcApi(preset.dcApi);
              }
              setHasChanges(true);
            }} className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${apiEnvironment === value ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`} data-id="element-679">
                  {label}
                </button>)}
            </div>
            <div className="grid sm:grid-cols-2 gap-4" data-id="element-680">
              <div data-id="element-681">
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-2" data-id="element-682">
                  Lawvo API
                </label>
                <input type="url" value={customLawvoApi} onChange={e => {
                setCustomLawvoApi(e.target.value);
                setApiEnvironmentState("custom");
                setHasChanges(true);
              }} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" data-id="element-683" />
              </div>
              <div data-id="element-684">
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-2" data-id="element-685">
                  Doc-Center API (Strapi)
                </label>
                <input type="url" value={customDcApi} onChange={e => {
                setCustomDcApi(e.target.value);
                setApiEnvironmentState("custom");
                setHasChanges(true);
              }} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" data-id="element-686" />
              </div>
            </div>
            <p className="text-xs text-slate-400" data-id="element-687">
              Local defaults: Lawvo API <code data-id="element-688">http://localhost:3030</code>,
              Doc-Center <code data-id="element-689">http://localhost:1367</code>. Uses shared{" "}
              <code data-id="element-690">lawvo_state</code> + <code data-id="element-691">dcState</code> JWT storage (same
              as lawvo-client and doc-center).
            </p>
          </div>
        </div>

        {/* API Key Section */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-692">
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50" data-id="element-693">
            <div className="flex items-center gap-2" data-id="element-694">
              <Key className="w-4 h-4 text-slate-500" data-id="element-695" />
              <h2 className="font-semibold text-slate-900" data-id="element-696">
                OpenRouter API Key
              </h2>
            </div>
            <p className="text-sm text-slate-500 mt-1" data-id="element-697">
              Required to generate AI translations. Your key is stored locally
              in your browser.
            </p>
          </div>
          <div className="p-6 space-y-4" data-id="element-698">
            <div data-id="element-699">
              <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-2" data-id="element-700">
                API Key
              </label>
              <div className="relative" data-id="element-701">
                <input type={showKey ? "text" : "password"} value={apiKey} onChange={e => handleApiKeyChange(e.target.value)} placeholder="sk-or-v1-..." className="w-full px-4 py-3 pr-12 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 font-mono transition-all" data-id="element-702" />

                <button onClick={() => setShowKey(!showKey)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 rounded transition-colors" data-id="element-703">
                  {showKey ? <EyeOff className="w-4 h-4" data-id="element-704" /> : <Eye className="w-4 h-4" data-id="element-705" />}
                </button>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-706">
              <div className={`w-2 h-2 rounded-full ${apiKey ? "bg-emerald-500" : "bg-slate-300"}`} data-id="element-707" />

              <span className="text-sm text-slate-600" data-id="element-708">
                {apiKey ? "API key is configured" : "No API key set"}
              </span>
            </div>
            <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-700 font-medium" data-id="element-709">
              Get your API key at openrouter.ai
              <ExternalLink className="w-3.5 h-3.5" data-id="element-710" />
            </a>
          </div>
        </div>

        {/* Models Section */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-711">
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between" data-id="element-712">
            <div data-id="element-713">
              <div className="flex items-center gap-2" data-id="element-714">
                <Cpu className="w-4 h-4 text-slate-500" data-id="element-715" />
                <h2 className="font-semibold text-slate-900" data-id="element-716">
                  Translation Models
                </h2>
              </div>
              <p className="text-sm text-slate-500 mt-1" data-id="element-717">
                Configure the 3 AI models used for parallel translation drafts
                via OpenRouter.
              </p>
            </div>
            <button onClick={handleResetModels} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-718">
              <RotateCcw className="w-3.5 h-3.5" data-id="element-719" />
              Reset to Defaults
            </button>
          </div>
          <div className="p-6 space-y-6" data-id="element-720">
            {models.map(model => {
            const colors = DRAFT_COLORS[model.draftId];
            return <div key={model.draftId} className={`rounded-lg border ${colors.border} ${colors.bg} p-5`} data-id="element-721">
                  <div className="flex items-center gap-2 mb-4" data-id="element-722">
                    <span className={`text-xs font-bold px-2 py-1 rounded ${colors.text} bg-white/80`} data-id="element-723">
                      Draft {model.draftId}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-724">
                    <div data-id="element-725">
                      <label className="block text-xs font-medium text-slate-600 mb-1.5" data-id="element-726">
                        Display Name
                      </label>
                      <input type="text" value={model.label} onChange={e => handleModelChange(model.draftId, "label", e.target.value)} placeholder="e.g. Claude" className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all" data-id="element-727" />
                    </div>
                    <div data-id="element-728">
                      <label className="block text-xs font-medium text-slate-600 mb-1.5" data-id="element-729">
                        OpenRouter Model ID
                      </label>
                      <input type="text" value={model.id} onChange={e => handleModelChange(model.draftId, "id", e.target.value)} placeholder="e.g. anthropic/claude-sonnet-4" className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm font-mono text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all" data-id="element-730" />
                    </div>
                  </div>
                </div>;
          })}

            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-731">
              <p className="text-xs font-medium text-slate-600 mb-2" data-id="element-732">
                Popular models on OpenRouter:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1" data-id="element-733">
                {[{
                id: "anthropic/claude-sonnet-4",
                name: "Claude Sonnet 4"
              }, {
                id: "openai/gpt-4o",
                name: "GPT-4o"
              }, {
                id: "google/gemini-2.5-flash-preview",
                name: "Gemini 2.5 Flash"
              }, {
                id: "anthropic/claude-3.5-sonnet",
                name: "Claude 3.5 Sonnet"
              }, {
                id: "openai/gpt-4o-mini",
                name: "GPT-4o Mini"
              }, {
                id: "google/gemini-2.5-pro-preview",
                name: "Gemini 2.5 Pro"
              }, {
                id: "meta-llama/llama-3.1-405b-instruct",
                name: "Llama 3.1 405B"
              }, {
                id: "deepseek/deepseek-r1",
                name: "DeepSeek R1"
              }].map(m => <div key={m.id} className="text-xs text-slate-500 py-0.5" data-id="element-734">
                    <span className="font-medium text-slate-700" data-id="element-735">{m.name}</span>{" "}
                    <span className="text-slate-400 font-mono" data-id="element-736">{m.id}</span>
                  </div>)}
              </div>
              <a href="https://openrouter.ai/models" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium mt-3" data-id="element-737">
                Browse all models on OpenRouter
                <ExternalLink className="w-3 h-3" data-id="element-738" />
              </a>
            </div>
          </div>
        </div>

        {/* Backup & Restore Section */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-739">
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50" data-id="element-740">
            <div className="flex items-center gap-2" data-id="element-741">
              <Archive className="w-4 h-4 text-slate-500" data-id="element-742" />
              <h2 className="font-semibold text-slate-900" data-id="element-743">Backup & Restore</h2>
            </div>
            <p className="text-sm text-slate-500 mt-1" data-id="element-744">
              Export your settings and glossary as a JSON file, or restore from
              a previous backup.
            </p>
          </div>
          <div className="p-6 space-y-5" data-id="element-745">
            {/* What's included */}
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-746">
              <p className="text-xs font-medium text-slate-600 mb-2" data-id="element-747">
                Backup includes:
              </p>
              <ul className="text-xs text-slate-500 space-y-1" data-id="element-748">
                <li className="flex items-center gap-2" data-id="element-749">
                  <Key className="w-3 h-3 text-slate-400" data-id="element-750" />
                  OpenRouter API key
                </li>
                <li className="flex items-center gap-2" data-id="element-751">
                  <Cpu className="w-3 h-3 text-slate-400" data-id="element-752" />
                  Model configurations (Draft A, B, C)
                </li>
                <li className="flex items-center gap-2" data-id="element-753">
                  <span className="w-3 h-3 text-slate-400 text-center font-bold leading-none" data-id="element-754">
                    G
                  </span>
                  Glossary entries ({glossary.length} terms)
                </li>
                <li className="flex items-center gap-2" data-id="element-755">
                  <Check className="w-3 h-3 text-slate-400" data-id="element-756" />
                  Segment overrides ({Object.keys(segmentOverrides).length}{" "}
                  segments)
                </li>
              </ul>
            </div>

            {/* Actions */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-757">
              <button onClick={handleExport} className="flex items-center justify-center gap-2 px-4 py-3 bg-white border-2 border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all" data-id="element-758">
                <Download className="w-4 h-4" data-id="element-759" />
                Export Backup
              </button>
              <button onClick={() => fileInputRef.current?.click()} className="flex items-center justify-center gap-2 px-4 py-3 bg-white border-2 border-dashed border-slate-300 rounded-lg text-sm font-medium text-slate-600 hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-700 transition-all" data-id="element-760">
                <Upload className="w-4 h-4" data-id="element-761" />
                Import Backup
              </button>
              <input ref={fileInputRef} type="file" accept=".json" onChange={handleImport} className="hidden" data-id="element-762" />
            </div>

            <p className="text-xs text-slate-400" data-id="element-763">
              Importing a backup will overwrite your current settings and
              glossary. This cannot be undone.
            </p>
          </div>
        </div>
      </div>

      {/* Restore Confirmation Modal */}
      {pendingBackup && <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" data-id="element-764">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden" data-id="element-765">
            <div className="p-6 border-b border-slate-100" data-id="element-766">
              <h3 className="text-lg font-semibold text-slate-900" data-id="element-767">
                Confirm Restore
              </h3>
              <p className="text-sm text-slate-500 mt-1" data-id="element-768">
                This will overwrite your current settings and glossary.
              </p>
            </div>
            <div className="p-6 space-y-4" data-id="element-769">
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 space-y-2 text-xs text-slate-600" data-id="element-770">
                <p className="font-medium text-slate-700 text-sm" data-id="element-771">
                  Backup contents:
                </p>
                <p data-id="element-772">
                  • API key:{" "}
                  {pendingBackup.settings?.apiKey ? "✓ Included" : "✗ Not set"}
                </p>
                <p data-id="element-773">
                  • Models:{" "}
                  {Array.isArray(pendingBackup.settings?.models) ? `${pendingBackup.settings.models.length} configured` : "None"}
                </p>
                <p data-id="element-774">• Glossary: {pendingBackup.glossary?.length || 0} entries</p>
                <p data-id="element-775">
                  • Translations:{" "}
                  {pendingBackup.segmentOverrides ? `${Object.keys(pendingBackup.segmentOverrides).length} segment overrides` : "None"}
                </p>
                {pendingBackup.exportedAt && <p className="text-slate-400 pt-1" data-id="element-776">
                    Exported{" "}
                    {new Date(pendingBackup.exportedAt).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric"
              })}
                  </p>}
              </div>

              {pendingBackup.segmentOverrides && Object.keys(pendingBackup.segmentOverrides).length > 0 && <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors" data-id="element-777">
                    <input type="checkbox" checked={restoreTranslations} onChange={e => setRestoreTranslations(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" data-id="element-778" />

                    <div data-id="element-779">
                      <p className="text-sm font-medium text-slate-900" data-id="element-780">
                        Restore translation overrides
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5" data-id="element-781">
                        Include{" "}
                        {Object.keys(pendingBackup.segmentOverrides).length}{" "}
                        approved segment translations. This will overwrite any
                        current translation progress.
                      </p>
                    </div>
                  </label>}
            </div>
            <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-3" data-id="element-782">
              <button onClick={() => setPendingBackup(null)} className="flex-1 py-2.5 border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-white transition-colors" data-id="element-783">
                Cancel
              </button>
              <button onClick={confirmRestore} className="flex-1 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors" data-id="element-784">
                Restore Backup
              </button>
            </div>
          </div>
        </div>}
    </div>;
};